import type { TouchEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { ArrowRightIcon } from '@twistopayments/design-system/icons/inline'

import type { SectionsListHorizontalComponent } from '@twisto/api-cms'

import { useInterval } from 'homepage/libs/hooks'

import { StrapiMedia } from '../../atoms'

import * as styles from './list-horizontal.styles'

const SLIDE_GAP = 24

export function ListHorizontal({
  listItemsHorizontal,
  autoSlidePeriod,
}: SectionsListHorizontalComponent) {
  const [slide, setSlide] = useState(0)
  const [slideWidth, setSlideWidth] = useState(0)
  const clientXStart = useRef<number | null>(null)

  const slidesAmount = listItemsHorizontal?.length ?? 0

  const next = () => setSlide((s) => (s + 1) % slidesAmount)
  const prev = () => setSlide((s) => (s === 0 ? slidesAmount - 1 : s - 1))

  useEffect(() => {
    const setSlides = () => {
      const listElement = document.querySelector('#list-horizontal')
      if (!listElement) {
        return
      }
      setSlideWidth((listElement.scrollWidth + SLIDE_GAP) / slidesAmount)
    }

    setSlides()
    window.addEventListener('resize', setSlides)

    return () => window.removeEventListener('resize', setSlides)
  }, [slidesAmount])

  const stopAutoSlide = useInterval(next, autoSlidePeriod ?? 0)

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    stopAutoSlide()
    clientXStart.current = e.changedTouches[0].clientX
  }
  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!clientXStart.current) {
      return
    }
    e.changedTouches[0].clientX < clientXStart.current ? next() : prev()
  }

  return (
    <section css={styles.wrap}>
      <div css={styles.nav}>
        <div css={styles.buttons}>
          <button
            onClick={() => {
              prev()
              stopAutoSlide()
            }}
          >
            <ArrowRightIcon color="white" stroke="#3BBAB2" />
          </button>
          <button
            onClick={() => {
              next()
              stopAutoSlide()
            }}
          >
            <ArrowRightIcon color="white" stroke="#3BBAB2" />
          </button>
        </div>

        <div css={styles.links}>
          {listItemsHorizontal?.map((slide, i) => (
            <button
              key={slide.id}
              css={styles.navItem}
              onClick={() => {
                setSlide(i)
                stopAutoSlide()
              }}
            >
              {slide.navigationText}
            </button>
          ))}
        </div>
      </div>

      <div css={styles.listOuter}>
        <div
          css={styles.list(SLIDE_GAP)}
          id="list-horizontal"
          style={{
            transform: `translateX(-${slide * slideWidth}px)`,
          }}
        >
          {listItemsHorizontal?.map((slide) => (
            <div
              key={slide.id}
              css={styles.slide}
              onTouchEnd={handleTouchEnd}
              onTouchStart={handleTouchStart}
            >
              <StrapiMedia {...slide.src} css={styles.img} />

              <div css={styles.text}>
                <div css={styles.title}>{slide.title}</div>
                <div css={styles.desc}>{slide.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
